import { loadBoard } from "frontend/services/boardsService";
import useStateValue from "frontend/state/value";
import { useEffect, useRef, useState } from "react";
import { Board, BoardPermission } from "shared/datamodel/schemas/board";
import useAuthentication from "./use-authentication";
import { authTokenAtom } from "state-atoms/general-atoms";
import { useSetAtom } from "jotai";

export default function useBoard({
  documentId,
  authRequired = false,
  password,
}: {
  documentId: string;
  authRequired?: boolean;
  password?: string;
}) {
  const [{ boards, teams, account }, dispatch] = useStateValue();
  const [board, setBoard] = useState<Board | null>(null);
  const isLoadedBoard = useRef(false);
  const [templateElements, setTemplateElements] = useState<Record<string, any> | null>(null);
  const [failedLoading, setFailedLoading] = useState(false);
  const [isDeletedBoard, setDeletedBoard] = useState(false);
  const [isPasswordProtected, setPasswordProtectedBoard] = useState(false);
  const [protectedBoardReady, setProtectedBoardReady] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [boardNotFound, setBoardNotFound] = useState(false);
  const { isReady, triggerAuthentication } = useAuthentication(authRequired);
  const setAuthToken = useSetAtom(authTokenAtom);

  useEffect(() => {
    // For maximum speed, attempt to load board immediately without waiting for auth
    // If auth is required, the API will return 401 and we'll handle it
    const boardFromCache = boards.find((board) => board.documentId === documentId);
    if (boardFromCache) {
      setBoard(boardFromCache);
      return;
    }

    if (!isLoadedBoard.current) {
      isLoadedBoard.current = true;
      loadBoard(dispatch, documentId, password)
        .then(async ({ board, templateElements, authToken }) => {
          setTemplateElements(templateElements);
          setBoard(board);
          setProtectedBoardReady(true);
          authToken && setAuthToken(authToken);
        })
        .catch((e) => {
          isLoadedBoard.current = false;
          if (e.response?.status == 303) {
            setPasswordProtectedBoard(true);
          }
          if (e.response?.status == 401) {
            triggerAuthentication();
          } else if (e.response?.status == 410) {
            setDeletedBoard(true);
          } else if (e.response?.status == 400) {
            setProtectedBoardReady(true);
            setFailedLoading(true);
          } else if (e.response?.status == 403) {
            setNoAccess(true);
            setProtectedBoardReady(true);
            setFailedLoading(true);
          } else if (e.response?.status == 404) {
            setNoAccess(true);
            setBoardNotFound(true);
            setFailedLoading(true);
          } else {
            // When an anonymous user access a board that doesn't exist,
            // the API returns 404. This is NOT the case for authenticated users.
            // In that case the API returns 403 even when the board does not exist.
            // TODO: This is really dumb and we should fix it.
            setFailedLoading(true);
          }
        });
    }
  }, [isReady, boards, password]);

  function checkAccessByAccount() {
    if (board?.accountId === account?.id) {
      return true;
    }
  }

  function hasAccessByTeamOrProject() {
    if (!checkAccessByAccount()) {
      return false;
    }
    if (!board?.projectDocumentId && !board?.teamDocumentId) {
      return true;
    }
    if (board?.projectDocumentId) {
      return teams?.some((t) => t.projects?.some((p) => p.documentId === board.projectDocumentId));
    }

    if (board?.teamDocumentId) {
      return teams?.some((t) => t.documentId === board.teamDocumentId);
    }

    return false;
  }

  function hasPublicAccess() {
    return board?.permission === BoardPermission.public || board?.permission === BoardPermission.publicReadOnly;
  }

  return {
    isReady,
    board,
    failedLoading,
    isDeletedBoard,
    boardNotFound,
    isPasswordProtected,
    protectedBoardReady,
    noAccess,
    templateElements,
    hasAccessByTeamOrProject,
    hasPublicAccess,
  };
}
